import FileTileView from './FileTileView';
import FolderTileView from './FolderTileView';
import PhotoTileView from './PhotoTileView';
import getAriaLabelForFileProviderItem from '../utils/getAriaLabelForFileProviderItem';
import { observer } from 'mobx-react';
import { FocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import {
    ISelection,
    Selection,
    SelectionMode,
    SelectionZone,
} from 'office-ui-fabric-react/lib/Selection';
import { formatUserDateTime } from 'owa-datetime';
import setThumbnailLoadState from '../../actions/setThumbnailLoadState';
import FileProviderItemViewState, {
    FileProviderItemType,
    FolderItemViewState,
    ImageLoadState,
} from '../../store/schema/FileProviderItemViewState';
import getIconCSSForFileProviderItem from '../../utils/getIconCSSForFileProviderItem';
import isFileProviderItemAnImage from '../../utils/isFileProviderItemAnImage';
import LoadMoreListView from 'owa-loadmore-listview/lib/components/LoadMoreListView';
import * as React from 'react';
import { IObjectWithKey } from 'office-ui-fabric-react/lib';

const styles = require('./FilePickerTileView.scss');
const classNames = require('classnames');

export interface FilePickerTileViewProps {
    dataSourceId: string;
    items: FileProviderItemViewState[];
    selectedItems: FileProviderItemViewState[];
    onLoadMoreRows: () => void;
    isLoadRowsInProgress: boolean;
    getCanLoadMore: () => boolean;
    onItemSelected?: (item: IObjectWithKey) => void;
    onItemsSelectionUpdated?: (selectedItems: FileProviderItemViewState[]) => void;
}

export interface FilePickerTileViewState {
    // The selection model is used to track the selection state of the files in the file picker
    selection: ISelection;
}

@observer
export default class FilePickerTileView extends React.Component<
    FilePickerTileViewProps,
    FilePickerTileViewState
> {
    // Variable that keeps track of whether the component is mounted or not
    private hasMounted: boolean;

    constructor(props: FilePickerTileViewProps) {
        super(props);

        this.state = {
            selection: new Selection({
                onSelectionChanged: this.onItemsSelectionChanged,
                getKey: this.getKeyForItem,
            }),
        };
    }

    render() {
        const itemIds = this.props.items.map(item => item.key);
        const listViewClassNames = classNames([styles.tilesContainer, 'customScrollBar']);

        return (
            <div className={styles.container}>
                <div className={styles.separator} />
                <FocusZone className={styles.focusZone}>
                    <SelectionZone
                        selection={this.state.selection}
                        selectionMode={SelectionMode.multiple}
                        onItemInvoked={this.props.onItemSelected}>
                        <LoadMoreListView
                            className={listViewClassNames}
                            dataSourceId={this.props.dataSourceId}
                            itemIds={itemIds}
                            onRenderRow={this.createItemView}
                            onLoadMoreRows={this.props.onLoadMoreRows}
                            isLoadRowsInProgress={this.props.isLoadRowsInProgress}
                            getCanLoadMore={this.props.getCanLoadMore}
                            currentLoadedIndex={itemIds.length}
                        />
                    </SelectionZone>
                </FocusZone>
            </div>
        );
    }

    componentDidMount() {
        // When the component mounts we need to set the items and the selected items
        // so that if the user switches between the view the selection is still maintained
        this.state.selection.setItems(this.props.items, true);
        this.resetSelectedItems(this.props.selectedItems, this.state.selection);
        this.hasMounted = true;
    }

    UNSAFE_componentWillReceiveProps(nextProps: FilePickerTileViewProps) {
        // We need to update the items in the selection when we load a new set of items, but
        // we don't want to reset it when we load more items (pagination).
        // This check prevents reseting the selection on pagination, while still keeping the selection up to date during folder navigation.
        // This also works (clears the state) if nextProps does not have items, as nextProps.items[0] will be undefined.
        if (this.props.items[0] !== nextProps.items[0]) {
            this.state.selection.setItems(nextProps.items, true /* shouldClear */);
        }

        // If something has changed in selected items then we reset the selection with the new selected items
        const shouldResetSelectedItems =
            nextProps.selectedItems.some(
                nextSelectedItem => this.props.selectedItems.indexOf(nextSelectedItem) === -1
            ) || nextProps.selectedItems.length === 0;
        if (shouldResetSelectedItems) {
            this.resetSelectedItems(nextProps.selectedItems, this.state.selection);
        }
    }

    componentWillUnmount() {
        this.hasMounted = false;
    }

    /* Called when the user selects items in the file picker */
    private onItemsSelectionChanged = () => {
        const selectedItems = this.state.selection.getSelection() as FileProviderItemViewState[];

        if (this.props.onItemsSelectionUpdated) {
            this.props.onItemsSelectionUpdated(selectedItems);
        }

        // Need to force update as the selection has changed if the component is mounted
        if (this.hasMounted) {
            this.forceUpdate();
        }
    };

    /* Use to create a key corresponding to an item when it is selected */
    private getKeyForItem(item: any, index: number): string {
        return index.toString();
    }

    private resetSelectedItems = (
        selectedItems: FileProviderItemViewState[],
        selection: ISelection
    ) => {
        // We need to disable the trigger of change event so during the process of resetting the selection
        selection.setChangeEvents(false);

        // Setting all items to unselected state
        selection.setAllSelected(false);

        // Setting the anchor to the first item
        this.state.selection.setIndexSelected(0, false /* isSelected */, true /* shouldAnchor */);

        // Getting the indexes of selected items and then setting selection to true for them
        selectedItems
            .map(selectedItem => this.props.items.indexOf(selectedItem))
            .forEach(selectedItemIndex =>
                selection.setIndexSelected(
                    selectedItemIndex,
                    true /* isSelected */,
                    false /* shouldAnchor */
                )
            );

        // We enable the change events when we are done
        selection.setChangeEvents(true);
    };

    private createItemView = (itemId: string, itemIndex: number): JSX.Element => {
        const item: FileProviderItemViewState = this.props.items[itemIndex];
        const isSelected = this.state.selection.isIndexSelected(itemIndex);
        const onThumbnailLoadSuccessful = () => setThumbnailLoadState(item, ImageLoadState.Loaded);
        const onThumbnailLoadFailed = () =>
            setThumbnailLoadState(item, ImageLoadState.ErrorLoading);

        const itemIcon = getIconCSSForFileProviderItem(item);
        const itemAriaLabel = getAriaLabelForFileProviderItem(item);
        const secondaryText = this.getSecondaryText(item);

        if (isFileProviderItemAnImage(item) && item.thumbnail && item.thumbnail.url) {
            // If the item is an image and a thumbnail exists then use the photo tile view
            // otherwise we will use the regular file view
            return (
                <PhotoTileView
                    key={itemId}
                    itemIndex={itemIndex}
                    itemType={item.model.ProviderType}
                    isSelected={isSelected}
                    primaryText={item.name}
                    secondaryText={secondaryText}
                    location={item.location}
                    ariaLabel={itemAriaLabel}
                    icon={itemIcon}
                    thumbnailUrl={item.thumbnail.url}
                />
            );
        }

        if (item.type === FileProviderItemType.File) {
            return (
                <FileTileView
                    key={itemId}
                    itemIndex={itemIndex}
                    itemType={item.model.ProviderType}
                    isSelected={isSelected}
                    primaryText={item.name}
                    secondaryText={secondaryText}
                    location={item.location}
                    ariaLabel={itemAriaLabel}
                    icon={itemIcon}
                    thumbnailUrl={item.thumbnail ? item.thumbnail.url : null}
                    onThumbnailLoaded={onThumbnailLoadSuccessful}
                    onThumbnailError={onThumbnailLoadFailed}
                    thumbnailLoadState={item.thumbnail ? item.thumbnail.loadState : null}
                />
            );
        }

        if (item.type === FileProviderItemType.Folder) {
            const folderItem = item as FolderItemViewState;
            return (
                <FolderTileView
                    key={itemId}
                    itemIndex={itemIndex}
                    itemType={item.model.ProviderType}
                    isSelected={isSelected}
                    primaryText={item.name}
                    secondaryText={secondaryText}
                    location={item.location}
                    ariaLabel={itemAriaLabel}
                    icon={itemIcon}
                    childCount={folderItem.childCount}
                />
            );
        }

        throw new Error('Unexpected item type');
    };

    private getSecondaryText(item: FileProviderItemViewState): string {
        return item.modifiedTime ? formatUserDateTime(item.modifiedTime) : null;
    }
}
